import React, { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Layer from './Layer';
import { useOverlay } from './useOverlay';

/** Matches the slide transition in c3.css. */
const EXIT_MS = 280;

interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** One line under the title. */
  subtitle?: React.ReactNode;
  /** A Lucide icon, already sized. */
  icon?: React.ReactNode;
  /** Label of the close button. */
  closeLabel?: string;
  /** Controls kept in the head, left of the close button. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A panel that slides in from the right edge over the page (W1 · S4.1). It behaves like `Dialog`
 * (Escape, Tab kept inside, focus returned) and plays its exit before leaving the page.
 */
const SidePanel: React.FC<SidePanelProps> = (props) => {
  const [mounted, setMounted] = useState(props.open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (props.open) {
      setMounted(true);
      // One frame at rest off-screen, so the slide-in is a transition and not a first paint.
      const frame = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [props.open]);

  if (!mounted) return null;
  return (
    <Layer>
      <SidePanelBody {...props} shown={shown} />
    </Layer>
  );
};

const SidePanelBody: React.FC<SidePanelProps & { shown: boolean }> = ({
  open,
  shown,
  onClose,
  title,
  subtitle,
  icon,
  closeLabel = 'Close',
  actions,
  children,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  useOverlay({ panelRef, onClose, active: open });

  return (
    <>
      <div className={`c3-so-backdrop${shown ? ' open' : ''}`} onClick={onClose} />
      <aside
        ref={panelRef}
        className={`c3-slideover${shown ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="ui-panel-head">
          {icon}
          <div className="min-w-0">
            <h3 id={titleId}>{title}</h3>
            {subtitle && <div className="ui-panel-sub">{subtitle}</div>}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {actions}
            <button type="button" className="ui-close" onClick={onClose} aria-label={closeLabel}>
              <X size={16} strokeWidth={1.5} aria-hidden />
            </button>
          </div>
        </div>
        {children}
      </aside>
    </>
  );
};

export default SidePanel;
